import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import ConsentCheckboxes from '@/components/consent/ConsentCheckboxes';

const WAITLIST_CONSENT_TYPES = ['privacy_policy'];

/**
 * Диалог подписки на список ожидания: email + согласие на обработку ПД.
 * Использует POST /public/waitlist с полем `consent`, согласие пишется до
 * создания записи (см. consent-spec.md §4.2).
 */
export default function WaitlistSubscribeDialog({ open, onClose, itemName, defaultEmail = '', onSubmit }) {
  const [email, setEmail] = useState(defaultEmail);
  const [consentState, setConsentState] = useState({ allAccepted: false, acceptedTypes: [], types: [] });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setEmail(defaultEmail);
    setConsentState({ allAccepted: false, acceptedTypes: [], types: [] });
    setError('');
  };

  const handleClose = () => { reset(); onClose?.(); };

  const isEmailValid = /^\S+@\S+\.\S+$/.test(email);
  const consentReady = consentState.types.length === 0 || consentState.allAccepted;
  const canSubmit = isEmailValid && consentReady && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      const consentPayload = consentState.types.length > 0
        ? {
            documentTypes: consentState.acceptedTypes,
            accepted: consentState.allAccepted,
            metadata: { source: 'waitlist_dialog' }
          }
        : null;
      await onSubmit({ email, consent: consentPayload });
      handleClose();
    } catch (e) {
      setError(e?.message || 'Не удалось отправить подписку');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Уведомить о поступлении</DialogTitle>
          <DialogDescription>
            {itemName ? <>Товар: <span className="font-medium">{itemName}</span></> : null}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="waitlist-email">Email для уведомления</Label>
            <Input
              id="waitlist-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoFocus
            />
            {email && !isEmailValid && (
              <p className="text-xs text-red-600 mt-1">Введите корректный email</p>
            )}
          </div>

          <ConsentCheckboxes
            types={WAITLIST_CONSENT_TYPES}
            onChange={setConsentState}
          />

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={handleClose} disabled={submitting}>
              Отмена
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Подписаться
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
